import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserIcon, BriefcaseIcon, HomeIcon, PhoneIcon, MailIcon, MapPinIcon, CalendarIcon, TagIcon, CreditCardIcon, ClockIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export function ClientDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [expandedApp, setExpandedApp] = useState<string | null>(null);
  // Mock client data
  const client = {
    id: '1',
    fullName: 'Jane Smith',
    dateOfBirth: '1985-06-15',
    contactInfo: {
      phone: '(555) 987-6543',
      email: 'jane.smith@example.com',
      address: '456 Oak Ave, Somewhere, NY 54321',
      preferredContactMethod: 'Email'
    },
    employment: {
      employer: 'Tech Solutions Inc.',
      position: 'Senior Developer',
      yearsEmployed: 5,
      annualIncome: 120000,
      employmentType: 'Full-time'
    },
    creditScore: 745,
    tags: ['First-time buyer', 'Tech professional', 'Pre-approved'],
    notes: [{
      date: '2023-06-10',
      author: 'John Broker',
      content: 'Initial consultation completed. Client is looking for a property in the $500-600k range.'
    }, {
      date: '2023-05-25',
      author: 'Sarah Agent',
      content: 'Pre-approval documents submitted to ABC Bank.'
    }, {
      date: '2023-05-15',
      author: 'John Broker',
      content: 'Client expressed interest in downtown condos and suburban houses.'
    }]
  };
  // Mock applications data
  const applications = [{
    id: 'APP-2023-001',
    type: 'Purchase',
    propertyAddress: '123 Main Street, Toronto, ON',
    propertyValue: 550000,
    loanAmount: 440000,
    stage: 'Approved',
    lenders: ['ABC Bank', 'XYZ Credit Union'],
    dates: {
      application: '2023-05-15',
      closing: '2023-07-30',
      renewal: null
    }
  }, {
    id: 'APP-2023-002',
    type: 'HELOC',
    propertyAddress: '456 Oak Ave, Somewhere, NY 54321',
    propertyValue: 600000,
    loanAmount: 100000,
    stage: 'Underwriting',
    lenders: ['DEF Financial'],
    dates: {
      application: '2023-06-05',
      closing: null,
      renewal: null
    }
  }, {
    id: 'APP-2022-015',
    type: 'Refinance',
    propertyAddress: '789 Pine Rd, Elsewhere, CA 98765',
    propertyValue: 480000,
    loanAmount: 360000,
    stage: 'Funded',
    lenders: ['GHI Mortgage'],
    dates: {
      application: '2022-11-10',
      closing: '2022-12-15',
      renewal: '2027-12-15'
    }
  }];
  const toggleExpand = (appId: string) => {
    if (expandedApp === appId) {
      setExpandedApp(null);
    } else {
      setExpandedApp(appId);
    }
  };
  // Helper function to render application stage with appropriate styling
  const renderStage = (stage: string) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    switch (stage) {
      case 'Lead':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'Pre-Qual':
        bgColor = 'bg-cyan-100';
        textColor = 'text-cyan-800';
        break;
      case 'Submitted':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Underwriting':
        bgColor = 'bg-orange-100';
        textColor = 'text-orange-800';
        break;
      case 'Approved':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Funded':
        bgColor = 'bg-purple-100';
        textColor = 'text-purple-800';
        break;
      case 'Declined':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
    }
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`} data-id="element-1146">
        {stage}
      </span>;
  };
  return <div data-id="element-1147">
      <div className="flex justify-between items-center mb-6" data-id="element-1148">
        <h1 className="text-2xl font-bold" data-id="element-1149">Client Details</h1>
        <div className="space-x-2" data-id="element-1150">
          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" data-id="element-1151">
            Edit
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" data-id="element-1152">
            New Application
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1153">
        {/* Client Profile Section */}
        <div className="lg:col-span-1 space-y-6" data-id="element-1154">
          {/* Basic Info Card */}
          <div className="bg-white rounded-lg shadow p-6" data-id="element-1155">
            <div className="flex items-center mb-4" data-id="element-1156">
              <div className="h-16 w-16 rounded-full bg-neutral-200 flex items-center justify-center mr-4" data-id="element-1157">
                <UserIcon className="h-8 w-8 text-neutral-500" data-id="element-1158" />
              </div>
              <div data-id="element-1159">
                <h2 className="text-xl font-medium" data-id="element-1160">{client.fullName}</h2>
                <div className="flex items-center text-sm text-neutral-500" data-id="element-1161">
                  <CalendarIcon className="h-4 w-4 mr-1" data-id="element-1162" />
                  <span data-id="element-1163">
                    DOB: {new Date(client.dateOfBirth).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            <div className="border-t border-neutral-200 pt-4 mt-4" data-id="element-1164">
              <h3 className="text-lg font-medium mb-3" data-id="element-1165">Contact Information</h3>
              <div className="space-y-3" data-id="element-1166">
                <div className="flex items-start" data-id="element-1167">
                  <PhoneIcon className="h-5 w-5 text-neutral-400 mr-2 mt-0.5" data-id="element-1168" />
                  <div data-id="element-1169">
                    <p className="text-sm text-neutral-500" data-id="element-1170">Phone</p>
                    <p className="font-medium" data-id="element-1171">{client.contactInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1172">
                  <MailIcon className="h-5 w-5 text-neutral-400 mr-2 mt-0.5" data-id="element-1173" />
                  <div data-id="element-1174">
                    <p className="text-sm text-neutral-500" data-id="element-1175">Email</p>
                    <p className="font-medium" data-id="element-1176">{client.contactInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1177">
                  <MapPinIcon className="h-5 w-5 text-neutral-400 mr-2 mt-0.5" data-id="element-1178" />
                  <div data-id="element-1179">
                    <p className="text-sm text-neutral-500" data-id="element-1180">Address</p>
                    <p className="font-medium" data-id="element-1181">{client.contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1182">
                  <UserIcon className="h-5 w-5 text-neutral-400 mr-2 mt-0.5" data-id="element-1183" />
                  <div data-id="element-1184">
                    <p className="text-sm text-neutral-500" data-id="element-1185">
                      Preferred Contact Method
                    </p>
                    <p className="font-medium" data-id="element-1186">
                      {client.contactInfo.preferredContactMethod}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Employment & Financial Info Card */}
          <div className="bg-white rounded-lg shadow p-6" data-id="element-1187">
            <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-1188">
              <BriefcaseIcon className="h-5 w-5 mr-2 text-neutral-500" data-id="element-1189" />
              Employment & Income Details
            </h3>
            <div className="space-y-3" data-id="element-1190">
              <div data-id="element-1191">
                <p className="text-sm text-neutral-500" data-id="element-1192">Employer</p>
                <p className="font-medium" data-id="element-1193">{client.employment.employer}</p>
              </div>
              <div data-id="element-1194">
                <p className="text-sm text-neutral-500" data-id="element-1195">Position</p>
                <p className="font-medium" data-id="element-1196">{client.employment.position}</p>
              </div>
              <div data-id="element-1197">
                <p className="text-sm text-neutral-500" data-id="element-1198">Employment Type</p>
                <p className="font-medium" data-id="element-1199">
                  {client.employment.employmentType}
                </p>
              </div>
              <div data-id="element-1200">
                <p className="text-sm text-neutral-500" data-id="element-1201">Years Employed</p>
                <p className="font-medium" data-id="element-1202">{client.employment.yearsEmployed}</p>
              </div>
              <div data-id="element-1203">
                <p className="text-sm text-neutral-500" data-id="element-1204">Annual Income</p>
                <p className="font-medium" data-id="element-1205">
                  ${client.employment.annualIncome.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="border-t border-neutral-200 pt-4 mt-4" data-id="element-1206">
              <div className="flex items-center mb-2" data-id="element-1207">
                <CreditCardIcon className="h-5 w-5 mr-2 text-neutral-500" data-id="element-1208" />
                <h3 className="text-lg font-medium" data-id="element-1209">Credit Score</h3>
              </div>
              <div className="bg-neutral-100 rounded-full h-6 w-full" data-id="element-1210">
                <div className={`h-6 rounded-full flex items-center justify-end pr-2 text-xs font-medium text-white ${client.creditScore >= 740 ? 'bg-green-500' : client.creditScore >= 670 ? 'bg-blue-500' : client.creditScore >= 580 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                width: `${Math.min(client.creditScore / 850 * 100, 100)}%`
              }} data-id="element-1211">
                  {client.creditScore}
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-1 text-right" data-id="element-1212">
                {client.creditScore >= 740 ? 'Excellent' : client.creditScore >= 670 ? 'Good' : client.creditScore >= 580 ? 'Fair' : 'Poor'}
              </p>
            </div>
          </div>
          {/* Tags/Segments Card */}
          <div className="bg-white rounded-lg shadow p-6" data-id="element-1213">
            <h3 className="text-lg font-medium mb-4 flex items-center" data-id="element-1214">
              <TagIcon className="h-5 w-5 mr-2 text-neutral-500" data-id="element-1215" />
              Tags & Segments
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-1216">
              {client.tags.map((tag, index) => <span key={index} className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-1217">
                  {tag}
                </span>)}
              <button className="bg-neutral-100 text-neutral-600 text-xs font-medium px-2 py-0.5 rounded-full flex items-center" data-id="element-1218">
                <PlusIcon className="h-3 w-3 mr-1" data-id="element-1219" />
                Add Tag
              </button>
            </div>
          </div>
          {/* Notes/Activity Log Card */}
          <div className="bg-white rounded-lg shadow p-6" data-id="element-1220">
            <div className="flex justify-between items-center mb-4" data-id="element-1221">
              <h3 className="text-lg font-medium flex items-center" data-id="element-1222">
                <ClockIcon className="h-5 w-5 mr-2 text-neutral-500" data-id="element-1223" />
                Notes & Activity Log
              </h3>
              <button className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1224">
                Add Note
              </button>
            </div>
            <div className="space-y-4" data-id="element-1225">
              {client.notes.map((note, index) => <div key={index} className="border-b border-neutral-200 pb-3 last:border-0 last:pb-0" data-id="element-1226">
                  <div className="flex justify-between items-center mb-1" data-id="element-1227">
                    <p className="font-medium" data-id="element-1228">{note.author}</p>
                    <p className="text-xs text-neutral-500" data-id="element-1229">
                      {new Date(note.date).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-sm text-neutral-600" data-id="element-1230">{note.content}</p>
                </div>)}
            </div>
          </div>
        </div>
        {/* Applications Section */}
        <div className="lg:col-span-2" data-id="element-1231">
          <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1232">
            <div className="p-6 border-b border-neutral-200" data-id="element-1233">
              <div className="flex justify-between items-center" data-id="element-1234">
                <h2 className="text-xl font-medium" data-id="element-1235">Applications</h2>
                
              </div>
            </div>
            <div className="divide-y divide-neutral-200" data-id="element-1236">
              {applications.map(app => <div key={app.id} className="p-4" data-id="element-1237">
                  <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleExpand(app.id)} data-id="element-1238">
                    <div className="flex items-center" data-id="element-1239">
                      <div className="mr-3" data-id="element-1240">
                        {app.type === 'Purchase' && <HomeIcon className="h-6 w-6 text-blue-600" data-id="element-1241" />}
                        {app.type === 'Refinance' && <CreditCardIcon className="h-6 w-6 text-green-600" data-id="element-1242" />}
                        {app.type === 'HELOC' && <BriefcaseIcon className="h-6 w-6 text-purple-600" data-id="element-1243" />}
                        {app.type === 'Renewal' && <ClockIcon className="h-6 w-6 text-orange-600" data-id="element-1244" />}
                      </div>
                      <div data-id="element-1245">
                        <h3 className="font-medium flex items-center" data-id="element-1246">
                          {app.id}
                          <span className="ml-2 text-sm text-neutral-500" data-id="element-1247">
                            ({app.type})
                          </span>
                        </h3>
                        <p className="text-sm text-neutral-500 truncate max-w-xs" data-id="element-1248">
                          {app.propertyAddress}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4" data-id="element-1249">
                      {renderStage(app.stage)}
                      {expandedApp === app.id ? <ChevronUpIcon className="h-5 w-5 text-neutral-400" data-id="element-1250" /> : <ChevronDownIcon className="h-5 w-5 text-neutral-400" data-id="element-1251" />}
                    </div>
                  </div>
                  {expandedApp === app.id && <div className="mt-4 pl-9 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1252">
                      <div data-id="element-1253">
                        <p className="text-sm text-neutral-500" data-id="element-1254">
                          Property Value
                        </p>
                        <p className="font-medium" data-id="element-1255">
                          ${app.propertyValue.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1256">
                        <p className="text-sm text-neutral-500" data-id="element-1257">Loan Amount</p>
                        <p className="font-medium" data-id="element-1258">
                          ${app.loanAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1259">
                        <p className="text-sm text-neutral-500" data-id="element-1260">Lender(s)</p>
                        <p className="font-medium" data-id="element-1261">{app.lenders.join(', ')}</p>
                      </div>
                      <div data-id="element-1262">
                        <p className="text-sm text-neutral-500" data-id="element-1263">LTV Ratio</p>
                        <p className="font-medium" data-id="element-1264">
                          {Math.round(app.loanAmount / app.propertyValue * 100)}
                          %
                        </p>
                      </div>
                      <div data-id="element-1265">
                        <p className="text-sm text-neutral-500" data-id="element-1266">
                          Application Date
                        </p>
                        <p className="font-medium" data-id="element-1267">
                          {new Date(app.dates.application).toLocaleDateString()}
                        </p>
                      </div>
                      <div data-id="element-1268">
                        <p className="text-sm text-neutral-500" data-id="element-1269">Closing Date</p>
                        <p className="font-medium" data-id="element-1270">
                          {app.dates.closing ? new Date(app.dates.closing).toLocaleDateString() : 'Not set'}
                        </p>
                      </div>
                      {app.dates.renewal && <div data-id="element-1271">
                          <p className="text-sm text-neutral-500" data-id="element-1272">
                            Renewal Date
                          </p>
                          <p className="font-medium" data-id="element-1273">
                            {new Date(app.dates.renewal).toLocaleDateString()}
                          </p>
                        </div>}
                      <div className="md:col-span-2 mt-2" data-id="element-1274">
                        <Link to={`/applications/${app.id}`} className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-1275">
                          View Full Application Details →
                        </Link>
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}